import type { Deal, PreInspectionData } from "../../lib/types";

/**
 * Compact summary of the RS pre-inspection findings.
 *
 * Used on:
 *   - DealDetail Overview tab
 *   - ChadApprovalsTab (build-approval queue cards)
 *   - ScheduleBuildModal (Robert's "schedule build" dialog)
 *   - PMBoardTab per-deal workbook (install / build day)
 *
 * Variants:
 *   - "card"    — full bordered panel with header + chips + descriptions
 *   - "inline"  — chip strip + truncated descriptions (for compact list views)
 *
 * Yes-answers on gutter guards / siding rot / satellite render as amber chips
 * so they can't be missed before tear-off. Nothing here is editable —
 * editing happens via the PRE Done modal on stage transition.
 */

type Variant = "card" | "inline";

interface Props {
  deal: Pick<Deal, "pre_inspection_data" | "pre_inspection_findings" | "pre_inspection_completed">;
  variant?: Variant;
  className?: string;
}

const YN_FIELDS: Array<{ key: keyof Pick<PreInspectionData, "gutter_guards" | "siding_rot" | "satellite">; label: string; }> = [
  { key: "gutter_guards", label: "Gutter Guards" },
  { key: "siding_rot",    label: "Siding Rot" },
  { key: "satellite",     label: "Satellite" },
];

function YnChip({ label, value }: { label: string; value: boolean | null }) {
  if (value === null || value === undefined) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-500 border border-slate-200">
        {label}: <span className="font-medium">—</span>
      </span>
    );
  }
  if (value === true) {
    // Amber: requires action before tear-off
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800 border border-amber-300 font-semibold">
        ⚠ {label}: Yes
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600 border border-slate-200">
      {label}: No
    </span>
  );
}

function hasAnyData(d: Props["deal"]): boolean {
  const data = d.pre_inspection_data;
  if (data) {
    return (
      data.gutter_guards !== null && data.gutter_guards !== undefined
      || data.siding_rot !== null && data.siding_rot !== undefined
      || data.satellite !== null && data.satellite !== undefined
      || !!data.damage_description?.trim()
      || !!data.collateral_description?.trim()
      || !!data.notes?.trim()
    );
  }
  return !!d.pre_inspection_findings?.trim();
}

export default function PreFindingsCard({ deal, variant = "card", className = "" }: Props) {
  if (!hasAnyData(deal)) return null;

  const data = deal.pre_inspection_data;
  const legacyText = !data ? deal.pre_inspection_findings?.trim() : null;

  const yesCount = data
    ? YN_FIELDS.filter((f) => data[f.key] === true).length
    : 0;

  if (variant === "inline") {
    return (
      <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
        {data
          ? YN_FIELDS.map((f) => <YnChip key={f.key} label={f.label} value={data[f.key]} />)
          : null}
        {legacyText && (
          <span className="text-xs text-slate-600 italic truncate max-w-[24rem]">
            {legacyText}
          </span>
        )}
      </div>
    );
  }

  // "card" variant
  return (
    <div className={`border rounded-lg overflow-hidden ${yesCount > 0 ? "border-amber-300 bg-amber-50/40" : "border-slate-200 bg-white"} ${className}`}>
      <div className={`px-3 py-2 flex items-center justify-between gap-2 ${yesCount > 0 ? "bg-amber-100/70" : "bg-slate-50"}`}>
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-600">PRE Findings</span>
          {yesCount > 0 && (
            <span className="text-[10px] font-semibold uppercase text-amber-800 bg-amber-200 rounded-full px-2 py-0.5">
              {yesCount} flag{yesCount === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </div>

      <div className="px-3 py-2.5 space-y-2.5">
        {/* Y/N chips */}
        {data ? (
          <div className="flex flex-wrap gap-1.5">
            {YN_FIELDS.map((f) => <YnChip key={f.key} label={f.label} value={data[f.key]} />)}
          </div>
        ) : null}

        {/* Description fields */}
        {data?.damage_description?.trim() && (
          <Field label="Damage" value={data.damage_description} />
        )}
        {data?.collateral_description?.trim() && (
          <Field label="Collateral" value={data.collateral_description} />
        )}
        {data?.notes?.trim() && (
          <Field label="Notes" value={data.notes} />
        )}

        {/* Legacy fallback: deals captured before structured form rolled out */}
        {legacyText && (
          <Field label="Notes (legacy)" value={legacyText} />
        )}
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-0.5">{label}</div>
      <div className="text-sm text-bardin-ink whitespace-pre-wrap">{value}</div>
    </div>
  );
}
